import apiClient from "./config";
import { CHAT_ENDPOINTS } from "./endpoints";

/**
 * Chat Service
 * Centralized API calls for messaging/chat features
 * Handles all conversation and message operations
 */
const chatService = {
  // ============= CONVERSATIONS =============

  /**
   * Get all conversations for authenticated user
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 20)
   * @returns {Promise} - Conversations list with pagination
   */
  getConversations: (page = 1, limit = 20) => {
    return apiClient.get(CHAT_ENDPOINTS.GET_CONVERSATIONS, {
      params: { page, limit },
    });
  },

  /**
   * Get conversation by ID
   * @param {number} conversationId - Conversation ID
   * @returns {Promise} - Conversation details with participants
   */
  getConversationById: (conversationId) => {
    return apiClient.get(CHAT_ENDPOINTS.GET_CONVERSATION_BY_ID(conversationId));
  },

  /**
   * Start a conversation with a user
   * @param {number} userId - User ID to start conversation with
   * @returns {Promise} - Created or existing conversation
   */
  startConversation: (userId) => {
    return apiClient.post(CHAT_ENDPOINTS.START_CONVERSATION(userId));
  },

  // ============= MESSAGES =============

  /**
   * Get messages in a conversation
   * @param {number} conversationId - Conversation ID
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 50)
   * @returns {Promise} - Messages list with pagination
   */
  getMessages: (conversationId, page = 1, limit = 50) => {
    return apiClient.get(CHAT_ENDPOINTS.GET_MESSAGES(conversationId), {
      params: { page, limit },
    });
  },

  // ============= MARK AS READ =============

  /**
   * Mark a message as read
   * @param {number} messageId - Message ID
   * @returns {Promise} - Updated message
   */
  markMessageAsRead: (messageId) => {
    return apiClient.put(CHAT_ENDPOINTS.MARK_MESSAGE_AS_READ(messageId));
  },

  /**
   * Mark all messages in conversation as read
   * @param {number} conversationId - Conversation ID
   * @returns {Promise} - Updated count
   */
  markAllAsRead: (conversationId) => {
    return apiClient.put(CHAT_ENDPOINTS.MARK_ALL_AS_READ(conversationId));
  },

  // ============= UNREAD COUNT =============

  /**
   * Get unread message count
   * @returns {Promise} - Unread count
   */
  getUnreadCount: () => {
    return apiClient.get(CHAT_ENDPOINTS.GET_UNREAD_COUNT);
  },

  // ============= SEARCH =============

  /**
   * Search messages
   * @param {string} searchTerm - Search term
   * @param {number} limit - Maximum results (default: 50)
   * @returns {Promise} - Search results
   */
  searchMessages: (searchTerm, limit = 50) => {
    return apiClient.get(CHAT_ENDPOINTS.SEARCH_MESSAGES, {
      params: { search_term: searchTerm, limit },
    });
  },

  // ============= FILE UPLOAD =============

  /**
   * Upload image for chat message
   * @param {File} imageFile - Image file to upload
   * @returns {Promise} - Upload result with file_url
   */
  uploadImage: (imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    
    return apiClient.post(CHAT_ENDPOINTS.UPLOAD_IMAGE, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // ============= DELETE =============

  /**
   * Delete a conversation
   * @param {number} conversationId - Conversation ID to delete
   * @returns {Promise} - Delete result
   */
  deleteConversation: (conversationId) => {
    return apiClient.delete(CHAT_ENDPOINTS.DELETE_CONVERSATION(conversationId));
  },

  /**
   * Edit a message
   * @param {number} messageId - Message ID to edit
   * @param {string} message - New message content
   * @returns {Promise} - Updated message
   */
  editMessage: (messageId, message) => {
    return apiClient.put(CHAT_ENDPOINTS.EDIT_MESSAGE(messageId), { message });
  },

  /**
   * Delete a message
   * @param {number} messageId - Message ID to delete
   * @returns {Promise} - Deleted message
   */
  deleteMessage: (messageId) => {
    return apiClient.delete(CHAT_ENDPOINTS.DELETE_MESSAGE(messageId));
  },
};

export default chatService;
